/**
 * Bulk price-adjustment dialog shared by the device and feature pricing
 * config grids — applies a flat-amount or percentage change to a
 * pricing row's default price and all of its overrides at once.
 */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

function AdjustPriceDialog({ open, onClose, entityLabel, defaultPrice, overridesCount, onApply }) {
  const { t } = useTranslation();
  const [adjustType, setAdjustType] = useState('amount'); // 'amount' or 'percentage'
  const [adjustValue, setAdjustValue] = useState('');
  const [adjustDirection, setAdjustDirection] = useState('increase'); // 'increase' or 'decrease'

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setAdjustType('amount');
      setAdjustValue('');
      setAdjustDirection('increase');
    }
  }, [open]);

  const handleApply = () => {
    const value = parseFloat(adjustValue);
    if (isNaN(value) || value <= 0) return;

    onApply({
      type: adjustType,
      value: adjustDirection === 'decrease' ? -value : value,
    });
    onClose();
  };

  const previewNewPrice = () => {
    if (!defaultPrice || !adjustValue) return null;
    const currentPrice = parseFloat(defaultPrice);
    const value = parseFloat(adjustValue);
    if (isNaN(value) || value <= 0) return null;

    let newPrice;
    if (adjustType === 'amount') {
      newPrice = adjustDirection === 'increase' ? currentPrice + value : currentPrice - value;
    } else {
      const multiplier = adjustDirection === 'increase' ? 1 + value / 100 : 1 - value / 100;
      newPrice = currentPrice * multiplier;
    }
    return Math.max(0, newPrice).toFixed(2);
  };

  const preview = previewNewPrice();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('billing.pricingConfig.adjustPriceTitle', { name: entityLabel })}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <RadioGroup row value={adjustDirection} onChange={e => setAdjustDirection(e.target.value)}>
            <FormControlLabel
              value="increase"
              control={<Radio size="small" />}
              label={t('billing.pricingConfig.adjustIncrease')}
            />
            <FormControlLabel
              value="decrease"
              control={<Radio size="small" />}
              label={t('billing.pricingConfig.adjustDecrease')}
            />
          </RadioGroup>

          <RadioGroup row value={adjustType} onChange={e => setAdjustType(e.target.value)}>
            <FormControlLabel
              value="amount"
              control={<Radio size="small" />}
              label={t('billing.pricingConfig.adjustAmountLabel')}
            />
            <FormControlLabel
              value="percentage"
              control={<Radio size="small" />}
              label={t('billing.pricingConfig.adjustPercentageLabel')}
            />
          </RadioGroup>

          <TextField
            label={
              adjustType === 'amount'
                ? t('billing.pricingConfig.adjustAmountLabel')
                : t('billing.pricingConfig.adjustPercentageLabel')
            }
            type="number"
            value={adjustValue}
            onChange={e => setAdjustValue(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: adjustType === 'amount' ? <InputAdornment position="start">$</InputAdornment> : null,
              endAdornment: adjustType === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : null,
            }}
            inputProps={{ min: 0, step: adjustType === 'amount' ? '0.01' : '1' }}
          />

          {defaultPrice && preview && (
            <Box sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 'var(--radius-md)' }}>
              <Typography variant="body2" color="text.secondary">
                {t('billing.pricingConfig.adjustPreview', {
                  from: `$${parseFloat(defaultPrice).toFixed(2)}`,
                  to: `$${preview}`,
                })}
              </Typography>
              {overridesCount > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {t('billing.pricingConfig.adjustOverridesCount', { count: overridesCount })}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('billing.pricingConfig.adjustCancel')}</Button>
        <Button variant="contained" onClick={handleApply} disabled={!adjustValue || parseFloat(adjustValue) <= 0}>
          {t('billing.pricingConfig.adjustApply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AdjustPriceDialog;
